import { motion } from "framer-motion";
import {
  Brain,
  Eye,
  MessageSquare,
  Cpu,
  Database,
  Cloud,
  GitMerge,
  Coins,
  ArrowRight,
  Sparkles,
  Search,
  CheckCircle,
  Code,
  LineChart,
  ShieldAlert,
  Leaf
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { AnimatedSection, FadeIn, AnimatedContainer, AnimatedItem } from "@/components/AnimatedSection";
import SEO from "@/components/SEO";
const expertiseAreas = [
  {
    icon: Brain,
    title: "Generative AI & LLMs",
    description: "Custom large language models, agentic reasoning workflows, retrieval-augmented generation (RAG), and domain-specific cognitive automation.",
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    badgeText: "Cognitive Scale",
    tagLine: "AI Core",
    tags: ["Custom LLMs", "Agentic Workflows", "RAG Systems"]
  },
  {
    icon: Eye,
    title: "Computer Vision",
    description: "Real-time video analytics, object detection, document intelligence (IDP), facial recognition, and automated quality inspection engines.",
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    badgeText: "Visual Analytics",
    tagLine: "Perception Engines",
    tags: ["Object Detection", "Video Analytics", "Document AI"]
  },
  {
    icon: MessageSquare,
    title: "Conversational AI",
    description: "Advanced natural language processing (NLP), semantic sentiment analysis, and highly contextual multilingual chat and voice systems.",
    color: "bg-emerald-50",
    iconColor: "text-emerald-600",
    badgeText: "Natural Language",
    tagLine: "Human-AI Interface",
    tags: ["Multilingual NLP", "Sentiment AI", "Voice Agents"]
  },
  {
    icon: Cpu,
    title: "ML Operations (MLOps)",
    description: "Robust model deployment, automated retraining pipelines, real-time drift monitoring, and high-throughput low-latency inference orchestration.",
    color: "bg-purple-50",
    iconColor: "text-purple-600",
    badgeText: "Pipeline Scale",
    tagLine: "Infrastructure",
    tags: ["CI/CD for ML", "Drift Detection", "Kubernetes"]
  },
  {
    icon: Database,
    title: "Data Governance & Eng",
    description: "Modern data lakes, secure ETL pipelines, real-time streaming analytics, enterprise data warehousing, and strict compliance governance.",
    color: "bg-rose-50",
    iconColor: "text-rose-600",
    badgeText: "Data Integrity",
    tagLine: "Foundational Data",
    tags: ["ETL Pipelines", "Data Lakes", "GDPR/HIPAA"]
  },
  {
    icon: Cloud,
    title: "Cloud Architecture",
    description: "Hybrid cloud orchestration, serverless execution setups, multi-region high-availability systems, and automated infrastructure as code.",
    color: "bg-sky-50",
    iconColor: "text-sky-600",
    badgeText: "Scale & Security",
    tagLine: "Cloud Native",
    tags: ["AWS/Azure/GCP", "Terraform", "Serverless"]
  },
  {
    icon: GitMerge,
    title: "Supply Chain Analytics",
    description: "Sales and Operations Planning (S&OP) digitization, consensus planning algorithms, multi-echelon inventory, and route logistics optimization.",
    color: "bg-indigo-50",
    iconColor: "text-indigo-600",
    badgeText: "Logistics Sync",
    tagLine: "Enterprise Functions",
    tags: ["S&OP Digitization", "Logistics", "Demand Forecast"]
  },
  {
    icon: Coins,
    title: "Finance Analytics",
    description: "AI invoicing engines, real-time invoice matching, fraud detection engines, and predictive analytics for cash flow and working capital.",
    color: "bg-teal-50",
    iconColor: "text-teal-600",
    badgeText: "Capital Precision",
    tagLine: "Enterprise Functions",
    tags: ["Automated Invoicing", "Fraud Detection", "Capital Models"]
  },
  {
    icon: Leaf,
    title: "ESG & Sustainability",
    description: "Automated carbon accounting, environmental compliance monitoring, supply chain ESG scoring, and structured ESG audit reports.",
    color: "bg-green-50",
    iconColor: "text-green-600",
    badgeText: "Green Compliance",
    tagLine: "Enterprise Functions",
    tags: ["Carbon Tracking", "ESG Audits", "Compliance"]
  }
];

const researchProjects = [
  {
    title: "MineMitra",
    type: "Sovereign Regulatory Q&A Bot",
    description: "A fully local compliance assistant matching natural language queries to regulation databases using text embedding cosine similarity.",
    badge: "State Mining",
    gradient: "from-amber-500 to-amber-700",
    link: "/case-studies/minemitra-chatbot"
  },
  {
    title: "Sovereign Document AI",
    type: "KYC & Fraud Detection API",
    description: "A fully on-premise Document Processing API with pixel-level tampering checks, KYC validation, and deep learning OCR.",
    badge: "National Bank",
    gradient: "from-blue-600 to-indigo-600",
    link: "/case-studies/onprem-document-ai"
  },
  {
    title: "WhatsApp Pitch Bot",
    type: "Multilingual Deck Generator",
    description: "An AI-powered WhatsApp bot enabling regional founders to create professional, high-quality pitch decks, breaking language barriers.",
    badge: "Startup Ecosystem",
    gradient: "from-emerald-500 to-teal-600",
    link: "/case-studies/whatsapp-ai-pitch-bot"
  },
  {
    title: "Sovereign ANPR & Vision",
    type: "Edge Mining & Traffic Monitor",
    description: "A fully offline dual-camera barricade monitoring system utilizing YOLOv4-Tiny, DeepSORT, and PaddleOCR to interdict illegal mineral transit.",
    badge: "Edge AI",
    gradient: "from-slate-600 to-slate-800",
    link: "/case-studies/sovereign-mining-detection"
  },
  {
    title: "AI Invoicing Engine",
    type: "Automated Financial Auditor",
    description: "An AI-powered invoicing system to automate financial workflows, handling massive volumes of invoices in multiple formats and languages.",
    badge: "Global Enterprise",
    gradient: "from-purple-600 to-rose-600",
    link: "/case-studies/ai-invoicing-engine"
  }
];

export default function ExpertisePage() {
  return (
    <div className="overflow-hidden bg-white" data-testid="expertise-page">
      {/* Hero Section */}
      <SEO
        title={"Deep Tech Expertise & AI Specializations | VANCO"}
        description={"Explore VANCO's expertise in generative AI, computer vision, conversational NLP, MLOps, cloud architecture and S&OP supply chain analytics."}
        path="/expertise"
      />
      <section className="relative pt-36 pb-20 bg-gradient-to-b from-[#f8fafc]/60 to-white" data-testid="expertise-hero">
        {/* Static Background Mesh Gradients */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute left-1/2 -translate-x-1/2 -top-64 w-[1100px] h-[500px] rounded-full bg-gradient-to-b from-[#f7c550]/40 via-[#fbbf24]/20 to-transparent blur-[100px]" />
          <div className="absolute left-[20%] -translate-x-1/2 -top-20 w-[700px] h-[450px] rounded-full bg-gradient-to-br from-[#60a5fa]/15 to-transparent blur-[90px]" />
          <div className="absolute right-[20%] translate-x-1/2 -top-20 w-[700px] h-[450px] rounded-full bg-gradient-to-br from-[#c084fc]/15 to-transparent blur-[90px]" />
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-1.5 border border-[#243b7f]/20 rounded-full text-[#243b7f] text-xs font-semibold mb-6 uppercase tracking-wider bg-white/40 backdrop-blur-sm font-season-mix">
              Our Capabilities
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#0f172a] font-season-mix mb-6">
              Enterprise AI & Technical{" "}
              <span className="text-[#243b7f]">Expertise</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
              We design, train, deploy, and scale production-ready artificial intelligence and full-stack software systems to address complex corporate operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Expertise Grid Section */}
      <AnimatedSection className="py-16 md:py-24 bg-white" data-testid="expertise-grid">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertiseAreas.map((area) => (
              <AnimatedItem key={area.title}>
                <Card className="h-full bg-white border border-slate-100 hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer group overflow-hidden">
                  <CardContent className="p-0 text-left">
                    {/* Colored Header */}
                    <div className={`${area.color} p-5 relative overflow-hidden`}>
                      <div className="flex items-start justify-between">
                        <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                          <area.icon className={`w-6 h-6 ${area.iconColor}`} />
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#243b7f] group-hover:translate-x-1 transition-all duration-300" />
                      </div>

                      {/* Badge */}
                      <div className="mt-4">
                        <span className={`inline-block px-3 py-1 bg-white/80 rounded-full text-xs font-bold ${area.iconColor} uppercase tracking-wider`}>
                          {area.badgeText}
                        </span>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-5">
                      <p className="text-xs text-slate-500 font-medium mb-1.5 uppercase tracking-wider">{area.tagLine}</p>
                      <h3 className="text-lg font-bold text-[#0f172a] font-season-mix mb-2 group-hover:text-[#243b7f] transition-colors leading-tight">
                        {area.title}
                      </h3>
                      <p className="text-slate-600 text-xs leading-relaxed mb-5 min-h-[64px]">
                        {area.description}
                      </p>

                      {/* Tag Badges */}
                      <div className="flex flex-wrap gap-1.5 mt-auto">
                        {area.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="bg-slate-50 text-slate-500 text-xs px-2 py-0 border-0">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* Products & Labs Section */}
      <AnimatedSection className="py-24 md:py-32 bg-slate-50" data-testid="research-lab">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-3 gap-12 items-center mb-16">
            <div className="lg:col-span-2 text-left">
              <span className="text-[#f7c550] font-semibold text-sm tracking-wide uppercase mb-4 block font-season-mix">
                Deployed Products
              </span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0f172a] font-season-mix mb-4">
                Our Products & Labs
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Beyond custom integration, VANCO develops and deploys AI products, edge systems, and automated frameworks to solve critical enterprise and regulatory challenges.
              </p>
            </div>
            <div className="flex justify-start lg:justify-end">
              <Sparkles className="w-16 h-16 text-[#243b7f]/20 animate-pulse" />
            </div>
          </div>

          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {researchProjects.map((project) => (
              <AnimatedItem key={project.title}>
                <Link to={project.link} className="h-full block">
                  <div className="h-full flex flex-col justify-between p-8 rounded-3xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 text-left relative overflow-hidden group h-full">
                    {/* Decorative faint background glow */}
                    <div className={`absolute -right-20 -top-20 w-40 h-40 rounded-full bg-gradient-to-br ${project.gradient} opacity-[0.03] group-hover:opacity-[0.08] transition-opacity duration-500`} />

                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <span className="inline-block px-3 py-1 bg-slate-100 rounded-full text-xs font-bold text-[#243b7f] uppercase tracking-wider">
                          {project.badge}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-[#0f172a] font-season-mix mb-2 group-hover:text-[#243b7f] transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-xs font-semibold text-[#243b7f] uppercase tracking-wider mb-4">
                        {project.type}
                      </p>
                      <p className="text-slate-600 text-sm leading-relaxed mb-6">
                        {project.description}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex items-center gap-2 text-[#243b7f] text-sm font-semibold group-hover:translate-x-1.5 transition-transform duration-300">
                      <span>View Case Study</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-white border-t border-slate-50" data-testid="expertise-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0f172a] font-season-mix mb-6">
              Ready to deploy specialized AI in your enterprise?
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Collaborate with our engineering and research teams to architect and launch production-ready models custom-fit for your operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-[#243b7f] hover:bg-[#1e3a8a] text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:shadow-lg"
                >
                  Schedule Technical Briefing
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
